import React, { useEffect, useMemo, useRef } from 'react';

import { useLocation } from 'react-router';

import { MenuObjectTypes } from 'enums/menu';
import { useImagesByChannels } from 'hooks/use-images-by-channels';
import { useCdpContext } from 'state/cdp';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/cdp/constants';
import { ICdpCtx } from 'state/cdp/types';
import { useMainMenuContext } from 'state/main-menu';
import { useLegacyMenuOptionsContext } from 'state/menu-options';
import { isExternalLink } from 'utils/is-external-link';
import { getMenuOptionUrl, handleMenuOptionPrefetch } from 'utils/menu';
import { routes } from 'utils/routing';

import { MenuListGridInner, MenuTileGridInner } from '../menu-tile-grid-container';
import { Tile } from '../tile';
import { TileLink, TileLinkExternal, TileListLink, TileListLinkExternal } from '../tile-link';

import {
  emitTileClickedEvent,
  getCaloriesAndPrice,
  getDescription,
  getImageFromOption,
} from './helpers';
import { useCalories } from './hooks';
import { IMenuTile } from './types';

const SECTION_TILE_STRING = 'section';
export const logClickEvent = (
  tileType: string,
  productId: string,
  productName: string,
  cdp: ICdpCtx
) => {
  if (tileType === SECTION_TILE_STRING) {
    cdp.trackEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.PRODUCT_SECTION_TILES,
        text: productName,
        componentId: productId,
      },
    });
  } else {
    cdp.trackEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.PRODUCTS,
        text: productName,
        componentId: productId,
      },
    });
  }
};

const NewMenuTile = ({
  enableMenuList,
  menuData,
  objectFitContain = false,
  enableDirectAccessToMenu,
}: IMenuTile) => {
  const firstMenuItemRef = useRef<HTMLAnchorElement | null>(null);
  const { pathname } = useLocation();
  const cdp = useCdpContext();

  const { getMenuObject } = useLegacyMenuOptionsContext();

  const getCalories = useCalories();
  const { getPricingAndAvailability, isStaticMenu } = useMainMenuContext();

  const MenuTileContainer = enableMenuList ? MenuListGridInner : MenuTileGridInner;
  const Link = enableMenuList ? TileListLink : TileLink;
  const LinkExternal = enableMenuList ? TileListLinkExternal : TileLinkExternal;

  const filteredMenuData = !isStaticMenu
    ? menuData.filter(option => getPricingAndAvailability(option._id)?.isAvailable)
    : menuData;

  useEffect(() => {
    // only pull focus to the first item if we are not on the main menu
    if (pathname !== routes.menu) {
      firstMenuItemRef.current?.focus();
    }
  }, [pathname]);

  return (
    <MenuTileContainer $enableDirectAccessToMenu={enableDirectAccessToMenu}>
      {filteredMenuData.map((option, menuItemIndex) => {
        const hideCalories = !!option?.hideCalories;

        const { calories, price } = getCaloriesAndPrice(option, getCalories, undefined, true); // we always enable the menu service data here because this is the "new" menu-tile

        const tile = (
          <Tile
            hideCalories={hideCalories}
            calories={calories}
            description={getDescription(option)}
            enableMenuList={enableMenuList}
            id={option._id}
            image={getImageFromOption(option)}
            imageDescription={option.imageDescription?.locale}
            name={option.name?.locale}
            objectFitContain={objectFitContain}
            price={price}
            type={option._type}
          />
        );

        const redirectUrl =
          option._type === MenuObjectTypes.SECTION && option?.redirectLink?.locale;

        if (redirectUrl && isExternalLink(redirectUrl)) {
          return (
            <LinkExternal key={option._id} href={redirectUrl}>
              {tile}
            </LinkExternal>
          );
        }

        const handleLinkClick = () => {
          emitTileClickedEvent({
            pathname,
            sanityId: option._id,
          });
          logClickEvent(option._type, option._id, option.name.locale, cdp);
        };

        return (
          <Link
            onMouseOver={handleMenuOptionPrefetch(getMenuObject, option)}
            onTouchStart={handleMenuOptionPrefetch(getMenuObject, option)}
            ref={menuItemIndex === 0 ? firstMenuItemRef : undefined}
            $transparent={option._type !== MenuObjectTypes.SECTION}
            key={option._id}
            aria-labelledby={option._id}
            onClick={handleLinkClick}
            to={redirectUrl || getMenuOptionUrl(option)}
          >
            {tile}
          </Link>
        );
      })}
    </MenuTileContainer>
  );
};

export const MenuTile = ({
  enableMenuList,
  menuData,
  objectFitContain = false,
  enableDirectAccessToMenu,
}: IMenuTile) => {
  const { changeImageByChannel } = useImagesByChannels();

  // flatten the sections and get only combos, items, pickers, etc.
  const menuItems = useMemo(() => {
    if (!enableDirectAccessToMenu) {
      return menuData;
    }

    return menuData.flatMap(item =>
      item._type === MenuObjectTypes.SECTION ? (item as any).options : item
    );
  }, [menuData, enableDirectAccessToMenu]);

  const prepareMenuData = changeImageByChannel(menuItems);

  return (
    <NewMenuTile
      enableMenuList={enableMenuList}
      menuData={prepareMenuData}
      objectFitContain={objectFitContain}
      enableDirectAccessToMenu={enableDirectAccessToMenu}
    />
  );
};
